// src/components/PaperPortfolio.jsx
// 복기 아이디어 — 롱/숏 조합을 시작일과 함께 넣어두고 구간 수익률을 추적한다.
//   종료일 없음 = 진행중 (오늘까지 계속 계산)
//   종료일 있음 = 기록 (그 구간만 계산)
//   [기록] 버튼 = 오늘 날짜로 종료 + 그 시점 수익률을 동결해서 보관
// 저장은 구글시트(Ideas 시트) — localStorage는 브라우저마다 따로라 폰에서 안 보임.
// 수익률은 /api/history 종가로 계산 (체결·수수료 없음).
import React, { useState, useEffect, useMemo, useRef } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid } from "recharts";
import { searchStocks } from "../lib/krStocks";
import { fetchIdeas, saveIdeas } from "../hooks/useSheets";

const LEGACY_KEY = "ls_paper";
const todayKST = () => new Date(Date.now() + 9 * 3600e3).toISOString().slice(0, 10);
const dstr = ts => new Date(ts * 1000 + 9 * 3600e3).toISOString().slice(0, 10);
const pct = n => `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
const pnlColor = n => (n >= 0 ? "#c0392b" : "#1f5fd0");

// 저장 결과 대조용 서명 — id/종료일/동결여부까지 비교해야 [기록] 실패를 잡는다.
const sigOf = arr => arr.map(x => `${x.id}|${x.endDate || ""}|${x.snapshot ? 1 : 0}`).sort().join(",");

// 시작일이 오래된 아이디어까지 종가가 닿도록 조회 범위를 늘린다.
function rangeFor(startDates) {
  if (!startDates.length) return "1y";
  const oldest = startDates.reduce((a, b) => (a < b ? a : b));
  const years = (Date.now() - new Date(oldest).getTime()) / (365.25 * 24 * 3600e3);
  if (years <= 0.9) return "1y";
  if (years <= 1.9) return "2y";
  if (years <= 4.9) return "5y";
  return "10y";
}

// 아이디어 1개의 시계열 수익률: 롱 바스켓 평균수익률 − 숏 바스켓 평균수익률 (동일가중, 달러중립)
// endDate가 있으면 그 날까지만 계산한다.
function computePort(port, series) {
  const legs = port.legs.map(l => {
    const pts = (series[l.c]?.points || []).filter(([ts, p]) => {
      if (!(p > 0)) return false;
      const d = dstr(ts);
      return d >= port.startDate && (!port.endDate || d <= port.endDate);
    });
    return { ...l, pts, base: pts.length ? pts[0][1] : null };
  });
  if (legs.some(l => !l.base) || !legs.length) return null;

  const longs = legs.filter(l => l.side === "L");
  if (!longs.length) return null;

  const maps = legs.map(l => new Map(l.pts.map(([ts, p]) => [ts, (p / l.base - 1) * 100])));
  const allTs = [...new Set(legs.flatMap(l => l.pts.map(([ts]) => ts)))].sort((a, b) => a - b);
  const last = new Array(legs.length).fill(0);
  const avg = idxs => idxs.reduce((s, i) => s + last[i], 0) / idxs.length;
  const li = legs.map((l, i) => (l.side === "L" ? i : -1)).filter(i => i >= 0);
  const si = legs.map((l, i) => (l.side === "S" ? i : -1)).filter(i => i >= 0);

  const rows = allTs.map(ts => {
    legs.forEach((l, i) => { if (maps[i].has(ts)) last[i] = maps[i].get(ts); });
    return { ts, ret: avg(li) - (si.length ? avg(si) : 0) };
  });
  const legRets = legs.map((l, i) => ({ c: l.c, n: l.n, side: l.side, ret: last[i] }));
  const rets = rows.map(r => r.ret);
  return {
    rows, legRets,
    now: rets[rets.length - 1],
    max: Math.max(...rets), min: Math.min(...rets),
    days: rows.length,
    lastDate: dstr(allTs[allTs.length - 1]),
  };
}

function LegSearch({ side, onPick }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const matches = open && q ? searchStocks(q, 6) : [];
  return (
    <div style={{ position: "relative", flex: 1, minWidth: 150 }}>
      <input value={q} placeholder={side === "L" ? "롱 종목" : "숏 종목"}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        style={{ width: "100%", padding: "6px 10px", borderRadius: 6, border: "1px solid #ddd", fontSize: 13, boxSizing: "border-box" }} />
      {matches.length > 0 && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20, background: "#fff", border: "1px solid #e2e2e2", borderRadius: 8, marginTop: 3, maxHeight: 210, overflowY: "auto", boxShadow: "0 6px 20px rgba(0,0,0,0.12)" }}>
          {matches.map(m => (
            <div key={m.c} onMouseDown={() => { onPick({ c: m.c, n: m.n, m: m.m, side }); setQ(""); setOpen(false); }}
              style={{ padding: "7px 10px", cursor: "pointer", fontSize: 13 }}
              onMouseEnter={e => e.currentTarget.style.background = "#f2f5fb"}
              onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
              <b>{m.n}</b> <span style={{ color: "#aaa" }}>{m.c}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// 아이디어 카드 — 진행중이면 라이브 계산, 기록이면 동결 스냅샷 우선.
function IdeaCard({ port, series, onClose, onDelete }) {
  const live = port.snapshot ? null : computePort(port, series);
  const r = port.snapshot || (live ? {
    now: live.now, max: live.max, min: live.min, days: live.days, lastDate: live.lastDate, legRets: live.legRets,
  } : null);
  const closed = !!port.endDate;

  return (
    <div style={{ border: "1px solid #eee", borderRadius: 10, padding: 14, background: closed ? "#fcfcfd" : "#fff" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 4,
              background: closed ? "#eceff1" : "#e7f5ec",
              color: closed ? "#78909c" : "#0a7c59",
            }}>{closed ? "종료" : "진행중"}</span>
            {port.name}
          </div>
          <div style={{ fontSize: 11, color: "#aaa" }}>
            {port.startDate} → {port.endDate || "진행중"}
            {r ? ` · ${r.days}거래일${closed ? "" : ` · ${r.lastDate} 기준`}` : ""}
            {port.snapshot ? " · 동결" : ""}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          {r && (
            <>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 10, color: "#aaa" }}>스프레드 수익률</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: pnlColor(r.now) }}>{pct(r.now)}</div>
              </div>
              <div style={{ fontSize: 11, color: "#999", lineHeight: 1.6 }}>
                <div>최고 {pct(r.max)}</div>
                <div>최저 {pct(r.min)}</div>
              </div>
            </>
          )}
          {!closed && r && (
            <button onClick={() => onClose(port, r)} title="오늘 날짜로 종료하고 수익률을 동결해 기록에 남깁니다"
              style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid #1a1a2e", background: "#1a1a2e", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              기록
            </button>
          )}
          <button onClick={() => onDelete(port)} title="삭제"
            style={{ background: "none", border: "none", cursor: "pointer", color: "#ccc", fontSize: 15 }}>✕</button>
        </div>
      </div>

      {!r ? (
        <div style={{ fontSize: 12, color: "#aaa", padding: "14px 0" }}>
          해당 구간 종가 데이터 대기 중 (장 마감 후 반영)
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 180px", gap: 14, marginTop: 10, alignItems: "center" }}>
          {live ? (
            <ResponsiveContainer width="100%" height={140}>
              <LineChart data={live.rows} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="#f6f6f6" vertical={false} />
                <XAxis dataKey="ts" tickFormatter={ts => dstr(ts).slice(5)} tick={{ fontSize: 9, fill: "#bbb" }} minTickGap={30} />
                <YAxis tick={{ fontSize: 9, fill: "#bbb" }} width={38} tickFormatter={v => `${v.toFixed(0)}%`} />
                <ReferenceLine y={0} stroke="#ccc" />
                <Tooltip labelFormatter={ts => dstr(ts)} formatter={v => [pct(v), "스프레드"]}
                  contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e8e8e8" }} />
                <Line type="monotone" dataKey="ret" stroke="#1a1a2e" strokeWidth={2} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ fontSize: 11, color: "#bbb", alignSelf: "center" }}>
              {port.startDate} ~ {port.endDate} 구간 확정 기록
            </div>
          )}
          <div style={{ fontSize: 12 }}>
            {r.legRets.map(l => (
              <div key={l.c} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
                <span style={{ color: "#666" }}>
                  <b style={{ color: l.side === "L" ? "#0a7c59" : "#c0392b" }}>{l.side}</b> {l.n}
                </span>
                <b style={{ color: pnlColor(l.ret) }}>{pct(l.ret)}</b>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PaperPortfolio() {
  const [ports, setPorts] = useState([]);
  const [series, setSeries] = useState({});
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [saveErr, setSaveErr] = useState(false);
  const [draft, setDraft] = useState({ legs: [], startDate: todayKST(), endDate: "", name: "" });
  const migrated = useRef(false);

  // Apps Script POST는 googleusercontent echo로 JSON 대신 HTML을 돌려주는 경우가 있어
  // 쓰기 응답은 신뢰하지 않고, 다시 읽어서 실제 반영됐는지 대조한다.
  const pushIdeas = next => {
    setSaveErr(false);
    return saveIdeas(next)
      .catch(() => {})
      .then(() => fetchIdeas())
      .then(d => {
        if (!d || d.error || !d.ideas || sigOf(d.ideas) !== sigOf(next)) setSaveErr(true);
      })
      .catch(() => setSaveErr(true));
  };

  // 시트에서 로드. 시트가 비어있고 예전 localStorage 기록이 있으면 한 번 올려준다.
  useEffect(() => {
    let cancelled = false;
    fetchIdeas()
      .then(d => {
        if (cancelled) return;
        // Apps Script는 모르는 action도 200 + {error}로 응답한다 (재배포 전 상태)
        if (!d || d.error || !d.ideas) { setStatus("error"); return; }
        const ideas = d.ideas;
        setStatus("ready");
        if (!ideas.length && !migrated.current) {
          migrated.current = true;
          let legacy = [];
          try { legacy = JSON.parse(localStorage.getItem(LEGACY_KEY) || "[]"); } catch { legacy = []; }
          if (legacy.length) {
            const up = legacy.map(p => ({ ...p, endDate: p.endDate || "", snapshot: p.snapshot || null }));
            setPorts(up);
            pushIdeas(up);
            return;
          }
        }
        setPorts(ideas);
      })
      .catch(() => { if (!cancelled) setStatus("error"); });
    return () => { cancelled = true; };
  }, []);

  const persist = next => {
    setPorts(next);
    pushIdeas(next);
  };

  // 동결 스냅샷이 없는 아이디어만 종가가 필요하다.
  const needPrice = useMemo(() => ports.filter(p => !p.snapshot), [ports]);
  const symKey = useMemo(() => {
    const m = new Map();
    needPrice.forEach(p => p.legs.forEach(l => m.set(l.c, `${l.c}:${l.m}`)));
    return [...m.values()].join(",");
  }, [needPrice]);
  const range = useMemo(() => rangeFor(needPrice.map(p => p.startDate)), [needPrice]);

  useEffect(() => {
    if (!symKey) { setSeries({}); return; }
    let cancelled = false;
    setLoading(true);
    fetch(`/api/history?symbols=${encodeURIComponent(symKey)}&range=${range}&interval=1d`)
      .then(r => r.json())
      .then(d => { if (!cancelled) setSeries(d.series || {}); })
      .catch(() => { if (!cancelled) setSeries({}); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [symKey, range]);

  const addLeg = leg => setDraft(d => (d.legs.some(l => l.c === leg.c) ? d : { ...d, legs: [...d.legs, leg] }));
  const removeLeg = c => setDraft(d => ({ ...d, legs: d.legs.filter(l => l.c !== c) }));

  const createPort = () => {
    if (!draft.legs.some(l => l.side === "L")) return;
    const auto = draft.legs.filter(l => l.side === "L").map(l => l.n).join("+")
      + (draft.legs.some(l => l.side === "S") ? " / " + draft.legs.filter(l => l.side === "S").map(l => l.n).join("+") : "");
    persist([...ports, {
      id: `${draft.startDate}-${draft.legs.map(l => l.side + l.c).join("")}`,
      name: draft.name.trim() || auto,
      startDate: draft.startDate,
      endDate: draft.endDate || "",
      legs: draft.legs,
      snapshot: null,
    }]);
    setDraft({ legs: [], startDate: todayKST(), endDate: "", name: "" });
  };

  // 기록 = 오늘로 종료 + 그 시점 수익률 동결
  const closePort = (port, r) => {
    persist(ports.map(p => (p.id === port.id ? {
      ...p,
      endDate: todayKST(),
      snapshot: { now: r.now, max: r.max, min: r.min, days: r.days, lastDate: r.lastDate, legRets: r.legRets },
    } : p)));
  };

  const deletePort = port => persist(ports.filter(p => p.id !== port.id));

  const running = ports.filter(p => !p.endDate);
  const archived = ports.filter(p => p.endDate);
  const canCreate = draft.legs.some(l => l.side === "L");
  const box = { background: "#fff", border: "1px solid #e8e8e8", borderRadius: 12, padding: 20, marginBottom: 24 };

  return (
    <div style={box}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#1a1a2e" }}>복기 아이디어 (롱숏 페어)</h3>
        <span style={{ fontSize: 11, color: saveErr ? "#c0392b" : "#aaa" }}>
          {saveErr ? "시트 저장 실패 — 새로고침 시 사라질 수 있음"
            : `진행중 ${running.length} · 기록 ${archived.length} · 종가 기준 · 시트 저장`}
        </span>
      </div>

      {/* 신규 아이디어 입력 */}
      <div style={{ background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: 8, padding: 14, marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <LegSearch side="L" onPick={addLeg} />
          <LegSearch side="S" onPick={addLeg} />
          <input value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
            placeholder="아이디어 이름(선택)"
            style={{ width: 140, padding: "6px 10px", borderRadius: 6, border: "1px solid #ddd", fontSize: 13 }} />
          <input type="date" value={draft.startDate} max={todayKST()}
            onChange={e => setDraft(d => ({ ...d, startDate: e.target.value }))}
            title="시작일 — 이 날 종가를 기준(0%)으로 잡습니다"
            style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ddd", fontSize: 13 }} />
          <input type="date" value={draft.endDate} max={todayKST()} min={draft.startDate}
            onChange={e => setDraft(d => ({ ...d, endDate: e.target.value }))}
            title="종료일 — 비워두면 진행중으로 계속 추적합니다"
            style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ddd", fontSize: 13, color: draft.endDate ? "#1a1a2e" : "#aaa" }} />
          <button onClick={createPort} disabled={!canCreate}
            style={{
              padding: "6px 14px", borderRadius: 6, border: "none", fontSize: 13, fontWeight: 600,
              background: canCreate ? "#1a1a2e" : "#ddd",
              color: "#fff", cursor: canCreate ? "pointer" : "default",
            }}>등록</button>
        </div>
        {draft.legs.length > 0 && (
          <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
            {draft.legs.map(l => (
              <div key={l.c} style={{
                display: "flex", alignItems: "center", gap: 5, padding: "4px 9px", borderRadius: 6, fontSize: 12,
                background: l.side === "L" ? "#e7f5ec" : "#fdecea",
                border: `1px solid ${l.side === "L" ? "#a5d6a7" : "#f5b7b1"}`,
              }}>
                <b style={{ color: l.side === "L" ? "#0a7c59" : "#c0392b" }}>{l.side === "L" ? "L" : "S"}</b>
                <span>{l.n}</span>
                <button onClick={() => removeLeg(l.c)} style={{ background: "none", border: "none", cursor: "pointer", color: "#aaa", fontSize: 12, padding: 0, lineHeight: 1 }}>✕</button>
              </div>
            ))}
          </div>
        )}
        <div style={{ marginTop: 8, fontSize: 11, color: "#aaa" }}>
          종료일을 비우면 진행중으로 계속 추적하고, [기록]을 누르면 그날 수익률로 동결해 아래 기록에 남깁니다.
        </div>
      </div>

      {status === "error" ? (
        <div style={{ textAlign: "center", color: "#c0392b", padding: "22px 0", fontSize: 13 }}>
          시트에서 아이디어를 불러오지 못했습니다 (Apps Script 재배포 확인)
        </div>
      ) : status === "loading" ? (
        <div style={{ textAlign: "center", color: "#aaa", padding: "22px 0", fontSize: 13 }}>불러오는 중…</div>
      ) : !ports.length ? (
        <div style={{ textAlign: "center", color: "#aaa", padding: "22px 0", fontSize: 13 }}>
          롱/숏 종목을 골라 등록하면 시작일 이후 수익률(롱 − 숏)을 추적합니다
        </div>
      ) : loading && !Object.keys(series).length ? (
        <div style={{ textAlign: "center", color: "#aaa", padding: "22px 0", fontSize: 13 }}>주가 불러오는 중…</div>
      ) : (
        <>
          {running.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {running.map(p => (
                <IdeaCard key={p.id} port={p} series={series} onClose={closePort} onDelete={deletePort} />
              ))}
            </div>
          )}

          {archived.length > 0 && (
            <>
              <div style={{ margin: "20px 0 10px", display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#888" }}>기록</span>
                <div style={{ flex: 1, height: 1, background: "#f0f0f0" }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {archived.map(p => (
                  <IdeaCard key={p.id} port={p} series={series} onClose={closePort} onDelete={deletePort} />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
